import React, { useState } from 'react'
import type { NavListItem, NavListProps } from './NavList.types'
import { NAV_LIST_DEMO_ITEMS, NAV_LIST_DEMO_TITLE } from './NavList.types'
import './NavList.css'

const NavList: React.FC<NavListProps> = ({
  title,
  items,
  value,
  defaultValue,
  onChange,
  showChevron = true,
  className = '',
  darkMode = false,
  previewState,
}) => {
  const isPreview = previewState != null
  const isControlled = value !== undefined

  const [uncontrolledValue, setUncontrolledValue] = useState<
    string | undefined
  >(defaultValue ?? NAV_LIST_DEMO_ITEMS[0]?.id)

  const resolvedItems = ((): NavListItem[] => {
    if (items != null) {
      return items
    }

    if (!isPreview) {
      return []
    }

    if (previewState === 'disabled-item') {
      return NAV_LIST_DEMO_ITEMS.map((item, index) =>
        index === 1 ? { ...item, disabled: true } : item,
      )
    }

    return NAV_LIST_DEMO_ITEMS
  })()

  const displayTitle = (() => {
    if (title !== undefined) {
      return title
    }
    if (previewState === 'no-title') {
      return undefined
    }
    if (isPreview) {
      return NAV_LIST_DEMO_TITLE
    }
    return undefined
  })()

  const resolvedShowChevron =
    previewState === 'no-chevron' ? false : showChevron

  const selectedId = (() => {
    if (isPreview) {
      switch (previewState) {
        case 'hover':
          return resolvedItems[0]?.id
        case 'no-title':
          return resolvedItems[2]?.id ?? resolvedItems[0]?.id
        case 'default':
        case 'selected':
        case 'no-chevron':
        case 'disabled-item':
        default:
          return resolvedItems[0]?.id
      }
    }

    if (isControlled) {
      return value
    }

    return uncontrolledValue
  })()

  const handleSelect = (id: string): void => {
    if (isPreview) {
      return
    }

    if (!isControlled) {
      setUncontrolledValue(id)
    }

    onChange?.(id)
  }

  const classNames = [
    'ds-nav-list',
    darkMode ? 'ds-nav-list--dark' : '',
    isPreview ? 'ds-nav-list--preview' : '',
    previewState ? `ds-nav-list--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const ariaLabel =
    typeof displayTitle === 'string' ? displayTitle : 'Navigation list'

  return (
    <nav className={classNames} aria-label={ariaLabel}>
      {displayTitle != null ? (
        <h3 className="ds-nav-list__title">{displayTitle}</h3>
      ) : null}
      <ul className="ds-nav-list__list" role="listbox" aria-label={ariaLabel}>
        {resolvedItems.map((item, index) => {
          const selected = item.id === selectedId
          const previewHover = previewState === 'hover' && index === 1

          const buttonClass = [
            'ds-nav-list__button',
            selected ? 'ds-nav-list__button--selected' : '',
            item.disabled ? 'ds-nav-list__button--disabled' : '',
            previewHover ? 'ds-nav-list__button--preview-hover' : '',
          ]
            .filter(Boolean)
            .join(' ')

          return (
            <li
              key={item.id}
              className="ds-nav-list__item"
              role="option"
              aria-selected={selected}
              aria-disabled={item.disabled || undefined}
            >
              <button
                type="button"
                className={buttonClass}
                disabled={item.disabled}
                tabIndex={isPreview ? -1 : undefined}
                onClick={() => handleSelect(item.id)}
              >
                <span className="ds-nav-list__label">{item.label}</span>
                {resolvedShowChevron ? (
                  <i
                    className="mgc mgc_right_small_fill ds-nav-list__chevron"
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default NavList
